import { setRef, useIsomorphicLayoutEffect } from '@cian/react-utils';

import { forwardRef, type JSX, type MouseEvent as ReactMouseEvent, useCallback, useMemo, useRef } from 'react';
import { clsx } from 'clsx';
import { createPortal } from 'react-dom';

import {
  type TAdfoxBannerCompleteSizes,
  type TAdfoxBannerOnLoadData,
  type TAdfoxTemplatesParams,
} from 'shared/types/adfox';
import { useApplicationContext } from 'shared/utils/applicationContext';

import { MainpageBrandContainer } from '../templates/MainpageBrand';
import { ParallaxGeneralContainer } from '../templates/ParallaxGeneral';
import { Picture } from '../templates/Picture';
import { Pixel } from '../templates/Pixel';
import { SidebarMapListing } from '../templates/SidebarMapListing';
import { SpecialProjectAnimatedContainer } from '../templates/SpecialProjectAnimated';
import { SpecialProjectContainer } from '../templates/SpecialProject';
import { TgbNativedescGeneralContainer } from '../templates/TgbNativedescGeneral';
import { TgbStretchContainer } from '../templates/TgbStretch';
import { useAdfoxBannerContext } from '../AdfoxBannerContext';

import { BannerSkeleton } from './internal/components/BannerSkeleton';
import { useBannerLoadingState } from './internal/hooks/useBannerLoadingState';
import { useBannerOverlay } from './internal/hooks/useBannerOverlay';
import { useHTMLBannerInserts } from './internal/hooks/useHTMLBannerInserts';

import styles from './AdfoxTemplates.css';

type TAdfoxTemplatesProps = {
  adfoxBannerOnLoadData: TAdfoxBannerOnLoadData | null;
  parsedDecodedParams: TAdfoxTemplatesParams | null;
  sizes: TAdfoxBannerCompleteSizes;
};

export const AdfoxTemplates = forwardRef<HTMLDivElement, TAdfoxTemplatesProps>((props, ref) => {
  const { adfoxBannerOnLoadData, parsedDecodedParams, sizes } = props;

  const bundleName = adfoxBannerOnLoadData?.bundleName;

  const { containerId, eventEmitter } = useAdfoxBannerContext();

  const { logger } = useApplicationContext();

  const isLoading = useBannerLoadingState({ eventEmitter });

  const rootRef = useRef<HTMLDivElement>(null);
  const setRootRef = useCallback(
    (node: HTMLDivElement) => {
      rootRef.current = node;
      setRef(ref, node);
    },
    [ref],
  );

  const handleClick = useCallback(
    (event: MouseEvent | ReactMouseEvent<HTMLElement, MouseEvent>): void =>
      eventEmitter.emit({ containerId, event: 'click', eventData: event }),
    [containerId, eventEmitter],
  );

  const templateElement = useMemo((): JSX.Element | null => {
    /**
     * @description Если нет parsedDecodedParams или bundleName не равен 'banner.transfer', то нет шаблона, что бы отобразить.
     */
    if (!parsedDecodedParams || bundleName !== 'banner.transfer') {
      return null;
    }

    /**
     * @description Если parsedDecodedParams не является валидным, то нет шаблона, что бы отобразить.
     */
    if (!isParsedParamsValid(parsedDecodedParams)) {
      logger.error('[Adfox] В JSON шаблона отсутствует обязательное поле type', {
        bundleName,
        domain: 'src/shared/components/AdfoxTemplates/AdfoxTemplates.tsx',
        parsedDecodedParams: JSON.stringify(parsedDecodedParams),
      });

      return null;
    }

    switch (parsedDecodedParams.type) {
      case 'parallax_general':
        return <ParallaxGeneralContainer context={parsedDecodedParams} />;

      case 'tgb_stretch':
        return <TgbStretchContainer context={parsedDecodedParams} />;

      case 'picture':
        return <Picture context={parsedDecodedParams} />;

      case 'pixel':
        return <Pixel />;

      case 'tgb_nativedesc_general':
        return <TgbNativedescGeneralContainer context={parsedDecodedParams} />;

      case 'special_project_banner':
        return <SpecialProjectContainer context={parsedDecodedParams} />;

      case 'special_project_banner_animate':
        return <SpecialProjectAnimatedContainer context={parsedDecodedParams} />;

      case 'mainpageBrand':
        return <MainpageBrandContainer context={parsedDecodedParams} />;

      case 'ad_sidebar_map':
        return <SidebarMapListing context={parsedDecodedParams} />;

      default: {
        // Если в TAdfoxTemplatesParams добавится новый тип без обработчика выше,
        // это присваивание упадёт на компиляции (parsedDecodedParams перестанет быть never).
        const exhaustiveCheck: never = parsedDecodedParams;

        logger.warning('[Adfox] Нет шаблона, чтобы отобразить этот тип', {
          bundleName,
          domain: 'src/shared/components/AdfoxTemplates/AdfoxTemplates.tsx',
          parsedDecodedParams: JSON.stringify(exhaustiveCheck),
        });

        return null;
      }
    }
  }, [bundleName, parsedDecodedParams, logger]);

  // templateElement вставляется через overlay-контейнер внутри рутового div + портал,
  // чтобы не перезаписывать разметку, вставленную библиотекой adfox. Портал (в отличие
  // от отдельного createRoot) сохраняет React-контекст, нужный шаблонам.
  const templateOverlay = useBannerOverlay({ enabled: templateElement != null, rootRef, zIndex: 1 });
  // Маркировка рекламы для HTML-баннеров — тоже overlay + портал.
  const htmlBannerInserts = useHTMLBannerInserts({ adfoxBannerOnLoadData, eventEmitter, rootRef });

  /**
   * @description Добавляет обработчик кликов на все anchor-элементы внутри баннера.
   * Это сделано потому, что у нас внутри баннера могут быть anchor-элементы, которые не являются потомками рутового div.
   * Например, в случае с mainpageBrand, у нас внутри баннера может быть anchor-элемент, который не является потомком рутового div.
   * Поэтому мы добавляем обработчик кликов на все anchor-элементы внутри баннера.
   * Через пропсы конечно прокинуть можно в каждый шаблон, но это не хорошо,
   * так как при добавлении нового шаблона, нужно будет добавлять обработчик кликов в каждый шаблон,
   * а про это можно забыть и в итоге получить баг.
   */
  useIsomorphicLayoutEffect(() => {
    if (templateOverlay) {
      const anchors = rootRef.current?.querySelectorAll<HTMLAnchorElement>('a');

      if (anchors) {
        anchors.forEach(anchor => {
          anchor.addEventListener('click', handleClick);
        });

        return (): void => {
          anchors.forEach(anchor => {
            anchor.removeEventListener('click', handleClick);
          });
        };
      }
    }

    return;
  }, [templateOverlay, handleClick]);

  return (
    // <aside> — семантически подходящий для рекламы элемент (контент, косвенно
    // связанный с основным). Является позиционирующим контейнером для скелетона.
    <aside
      className={clsx(
        styles['container'],
        !!templateOverlay && styles['container--json-banner'],
        isLoading && styles['container--loading'],
      )}
      style={sizes}
    >
      <div
        className={styles['root']}
        ref={setRootRef}
        id={containerId}
        onClick={templateOverlay ? undefined : handleClick}
      >
        {templateOverlay && templateElement && createPortal(templateElement, templateOverlay)}
        {htmlBannerInserts}
      </div>

      {isLoading && !templateOverlay && <BannerSkeleton />}
    </aside>
  );
});

AdfoxTemplates.displayName = 'AdfoxTemplates';

function isParsedParamsValid(params: object): params is TAdfoxTemplatesParams {
  return 'type' in params;
}
